import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	Dialog,
	DialogTitle,
	IconButton,
	DialogContent,
	Button,
	DialogActions,
	Select,
	MenuItem,

} from "@mui/material";
import { FaPaperPlane,FaEdit  } from "react-icons/fa";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { Close as CloseIcon } from "@mui/icons-material";
import { createUser, updateUser } from "../../redux/actions/user.actions";
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import TextField from '@mui/material/TextField';
import ButtonOnly from "../../components/Comman/button-new/buttonNew";


const BootstrapDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialogContent-root": { padding: theme.spacing(2) },
	"& .MuiDialogActions-root": { padding: theme.spacing(1) },
}));


function BootstrapDialogTitle(props) {
	const { children, onClose, ...other } = props;
	
	return (
		<DialogTitle sx={{ m: 0, p: 2 }} {...other}>
			{children}
			{onClose ? (
				<IconButton
					aria-label='close'
					onClick={onClose}
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}>
					<CloseIcon color="error"/>
				</IconButton>
			) : null}
		</DialogTitle>
	);
}

BootstrapDialogTitle.propTypes = {
	children: PropTypes.node,
	onClose: PropTypes.func.isRequired,
};



const schema = yup.object().shape({
	name: yup.string().required("Name is required"),
	email: yup.string().email().required("Email is required"),
	number: yup
		.string()
		.matches(/^[0-9]+$/, "Please enter a valid number")
		.required("Number is required")
		.trim(),
	password: yup
		.string()
		.min(
			12,
			"Password must be at least 12 characters long, with one uppercase letter, one lowercase letter, one digit, and one special character."
		)
		.matches(
			/^(?=.*[!@#$%^&*()\-_=+{};:,<.>])(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{12,}$/,
			"Password must include at least one uppercase letter, one lowercase letter, one digit, and one special character."
		)
		.required("Password is required")
		.trim(),
	role: yup.string().required("Role is required"),
})

const CreateModal = (props) => {
	const dispatch = useDispatch();

	const { openModal, handleModal, isEdit } = props;
	const { user } = useSelector((state) => state.userReducer);
	const handleClose = () => {
		handleModal();
	};

	const { register, handleSubmit, control, setValue, formState: { errors } } = useForm({
		resolver: yupResolver(schema)
	});

	const onSubmit = (data) => {
		const { number, ...rest } = data;
		const newData = { ...rest, mobile: parseInt(number, 10) };
		if (isEdit) {
			dispatch(updateUser(user.id, newData))
		} else {
			dispatch(createUser(newData))
		}
		handleModal(false)
	}

	const [showPassword, setShowPassword] = React.useState(false);
	const handleClickShowPassword = () => { setShowPassword((show) => !show) };
	const handleMouseDownPassword = (event) => {
		event.preventDefault();
	};




	useEffect(() => {
		if (isEdit) {
			setValue("name", user.name)
			setValue("email", user.email)
			setValue("number", user.number)
			setValue("role", user.role)
		}
	}, [isEdit, user, setValue]);

	return (

		<BootstrapDialog
			onClose={handleClose}
			aria-labelledby='customized-dialog-title'
			open={openModal}
		>
			<BootstrapDialogTitle id='customized-dialog-title' onClose={handleClose} className="text-center text-bg-info text-light">
				{isEdit ? `Update user` : `Add new user`}
			</BootstrapDialogTitle>
			<DialogContent dividers>
				<form onSubmit={handleSubmit(onSubmit)} className="container shadow-lg bg-white py-4">
					<div className="row d-flex justify-content-center align-items-center">
						<div className="col-md-6">
							<TextField
								{...register('name')}
								label="Name"
								error={!!errors.name}
								helperText={errors.name ? errors.name.message : ''}
								className="w-100"
							/>
						</div>
						<div className="col-md-6">
							<TextField
								{...register('email')}
								label="Email"
								error={!!errors.email}
								className="w-100"
								helperText={errors.email ? errors.email.message : ''}
							/>
						</div>
						<div className="col-md-6 my-3">
							<TextField
								type="text"
								{...register('number')}
								label="Number"
								inputProps={{
									maxLength: 10,
									onKeyPress: (e) => {
										const isValidInput = /^[0-9\b]+$/.test(e.key);
										if (!isValidInput) {
											e.preventDefault();
										}
									}
								}}
								className="w-100"
								error={!!errors.number}
								helperText={errors.number ? errors.number.message : ''}
							/>
						</div>
						<div className="col-md-6 my-3 position-relative ">
							<TextField
								type={showPassword ? "text" : "password"}
								{...register('password')}
								label="Password"
								error={!!errors.password}
								className="w-100"
								helperText={errors.password ? errors.password.message : ''}
								InputProps={{
									endAdornment: (
										<IconButton
											aria-label="toggle password visibility"
											onClick={handleClickShowPassword}
											onMouseDown={handleMouseDownPassword}
											edge="end"
										>
											{showPassword ? <Visibility /> : <VisibilityOff />}
										</IconButton>
									)
								}}
							/>
						</div>
						<div className="col-md-12 mb-3 d-flex justify-content-center align-items-center">
							<Controller
								control={control}
								name="role"
								render={({ field }) => (
									<Select
										{...field}
										label="Role"
										error={!!errors.role}
										defaultValue="" // Set default value to an empty string
										displayEmpty
									>
										<MenuItem value="" disabled>
											Select Role
										</MenuItem>
										<MenuItem value="Admin">Admin</MenuItem>
										<MenuItem value="User">User</MenuItem>
									</Select>
								)}
							/>
							{errors.role && <span className="text-danger">{errors.role.message}</span>}
						</div>
						<div className="col-md-6 d-flex justify-content-center">
							<ButtonOnly
								buttonLabel={isEdit ? "Update" : "Submit"}
								buttonIcon={isEdit ? <FaEdit  /> : <FaPaperPlane />}
								buttonClick={handleSubmit(onSubmit)}
							/>
						</div>
					</div>
				</form>
			</DialogContent>

			{props.location?.pathname === "/" ? "" : <DialogActions>
				<Button type='submit' onClick={handleSubmit}>
					Save
				</Button>
				<Button onClick={handleClose}>Close</Button>
			</DialogActions>
			}

		</BootstrapDialog>

	);
};

export default CreateModal;
